import { LocalizedText } from "../i18n/LocalizedCopy";
import { useSourceLanguage } from "../i18n/LocalizedCopy";
import useNavigationTranslations from "../i18n/useNavigationTranslations";
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import HomeFriendsFeed from "./HomeFriendsFeed";
import EternuaIcon, { iconForPath } from "./EternuaIcon";
import "./HomeNavigationLayout.css";

export const HOME_NAVIGATION_GROUPS = [
  {
    id: "today", title: "Aujourd’hui", subtitle: "Les essentiels du quotidien", icon: "home", defaultOpen: true, fallbackTo: "/aujourdhui",
    links: [
      { label: "Vue d’ensemble", to: "/aujourdhui" },
      { label: "Acheter", to: "/market/acheter?tab=parcourir" },
      { label: "Vendre", to: "/mes-eternua/vendre/classique" },
      { label: "Donner", to: "/market/donner?tab=donner" },
      { label: "Services", to: "/market/services" },
      { label: "Entraide · Premium Ultra", to: "/entraide", icon: "community" },
      { label: "Emploi", to: "/market/emploi" },
      { label: "Sorties", to: "/market/sortie" },
      { label: "Voyages", to: "/market/voyage" },
      { label: "Sport & loisirs", to: "/market/sport-loisir" },
      { label: "Babysitting", to: "/market/babysitting" },
      { label: "Animaux", to: "/market/animaux" },
      { label: "Immobilier & séjours", to: "/market/immobilier" },
      { label: "Crowdfunding", to: "/crowdfunding", icon: "infinity" },
      { label: "Forum", to: "/forum" },
      { label: "Amis", to: "/amis" },
      { label: "Agenda", to: "/mes-eternua/agenda" },
      { label: "Récompenses", to: "/mes-eternua" },
    ],
  },
  {
    id: "vision", title: "Vision3024", subtitle: "Imaginer les mondes de demain", icon: "infinity", defaultOpen: false, fallbackTo: "/vision-3024",
    links: [
      { label: "Explorer la vision", to: "/vision-3024" },
      { label: "France 3024", to: "/vision-3024/france-3024" },
      { label: "LIA du futur", to: "/vision-3024/lia", icon: "robot" },
      { label: "Infrastructures du futur", to: "/vision-3024/infrastructures", icon: "temple" },
      { label: "Santé éternelle", to: "/vision-3024/sante-eternelle", icon: "trust" },
      { label: "IA humaines", to: "/ia-humaines" },
      { label: "Civilisations E.T", to: "/temples/civilisations" },
    ],
  },
];

export function calculateHomeSidebarBounds({ headerBottom, layoutTop, layoutBottom, viewportBottom, dockTop = Infinity, footerTop = Infinity, gap = 16 }) {
  const top = Math.max(headerBottom + gap, layoutTop + gap);
  const bottom = Math.min(viewportBottom, dockTop, footerTop, layoutBottom) - gap;
  return { top, height: Math.max(0, bottom - top) };
}

/** Route-scoped layout. Missing destinations use the group's overview as a temporary link. */
export default function HomeNavigationLayout({ children, groups = HOME_NAVIGATION_GROUPS, initiallyCollapsed = false }) {
  useSourceLanguage();
  const { translateLabel } = useNavigationTranslations();
  const id = useId();
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 1100);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState("");
  const [expanded, setExpanded] = useState(() => Object.fromEntries(groups.map(group => [group.id, Boolean(group.defaultOpen)])));
  const panelRef = useRef(null);
  const layoutRef = useRef(null);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(undefined);
  const [panelTop, setPanelTop] = useState(undefined);

  useLayoutEffect(() => {
    if (mobile) return undefined;
    const visibleRect = element => {
      if (!element?.getClientRects().length) return null;
      const style = getComputedStyle(element);
      return style.visibility === "hidden" || style.display === "none" ? null : element.getBoundingClientRect();
    };
    const measure = () => {
      if (!layoutRef.current) return;
      const layout = layoutRef.current.getBoundingClientRect();
      const dockTops = [...document.querySelectorAll(".et-global-dock, .friends-dock")]
        .map(visibleRect).filter(rect => rect && rect.height > 0).map(rect => rect.top);
      const bounds = calculateHomeSidebarBounds({
        headerBottom: Math.max(0, visibleRect(document.querySelector(".et-global-nav"))?.bottom || 0),
        layoutTop: layout.top,
        layoutBottom: layout.bottom,
        viewportBottom: window.innerHeight,
        dockTop: Math.min(...dockTops),
        footerTop: visibleRect(document.querySelector(".eternua-site-footer"))?.top ?? Infinity,
      });
      setPanelTop(bounds.top);
      setAvailableHeight(bounds.height);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(document.body);
    document.querySelectorAll(".et-global-nav, .et-global-dock, .friends-dock, .eternua-site-footer").forEach(element => observer?.observe(element));
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure); };
  }, [mobile]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1099px)");
    const update = () => { setMobile(media.matches); setMobileOpen(false); };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!mobile || !mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKeyDown = event => {
      if (event.key === "Escape") { event.preventDefault(); setMobileOpen(false); }
      if (event.key === "Tab") {
        const elements = [...panelRef.current.querySelectorAll('button, a[href], input')].filter(el => !el.closest('[hidden]') && !el.disabled);
        const first = elements[0], last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const trigger = triggerRef.current;
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      if (trigger?.isConnected) trigger.focus();
    };
  }, [mobile, mobileOpen]);

  const compact = collapsed && !mobile;
  const normalizeQuery = value => String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase().trim();
  const query = normalizeQuery(menuQuery);
  const filteredGroups = query ? groups.map(group => ({ ...group, links: group.links.filter(item => normalizeQuery(`${item.label} ${translateLabel(item.label)} ${group.title} ${translateLabel(group.title)}`).includes(query)) })).filter(group => group.links.length) : groups;
  // Keep Explorer between the header and footer, above the fixed bottom dock.
  const columnStyle = { maxHeight: availableHeight, top: panelTop, visibility: availableHeight >= 160 ? undefined : "hidden" };
  const panel = (
    <aside id={`${id}-navigation`} ref={panelRef} className={`home-navigation${compact ? " home-navigation--compact" : !mobile && availableHeight < 360 ? " home-navigation--constrained" : ""}`}
      style={!mobile ? columnStyle : undefined}
      role={mobile ? "dialog" : undefined} aria-modal={mobile ? "true" : undefined} aria-label={translateLabel("Navigation de l’accueil")}>
      <div className="home-navigation__header">
        <div className="home-navigation__brand"><EternuaIcon name="infinity" /><span><small><LocalizedText source={"ETERNUA"} /></small><strong>{translateLabel("Explorer")}</strong></span></div>
        <button ref={closeRef} type="button" className="home-navigation__toggle"
          aria-label={translateLabel(mobile ? "Fermer le menu de l’accueil" : compact ? "Déplier la navigation de l’accueil" : "Replier la navigation de l’accueil")}
          title={translateLabel(mobile ? "Fermer" : compact ? "Déplier" : "Replier")}
          aria-expanded={!mobile ? !collapsed : undefined}
          onClick={() => mobile ? setMobileOpen(false) : setCollapsed(value => !value)}>
          <span aria-hidden="true">{mobile ? "×" : compact ? "»" : "«"}</span>
        </button>
      </div>
      {!compact && <div className="home-navigation__search">
        <label htmlFor={`${id}-search`}>{translateLabel("Trouver une rubrique")}</label>
        <input id={`${id}-search`} type="search" value={menuQuery} onChange={event => setMenuQuery(event.target.value)} placeholder={translateLabel("Services, voyages, projets…")} autoComplete="off" />
        <Link to="/recherche" onClick={() => setMobileOpen(false)}>{translateLabel("Rechercher sur tout Eternua")} →</Link>
        {query && !filteredGroups.length && <p role="status">{translateLabel("Aucune rubrique correspondante.")}</p>}
      </div>}
      <nav className="home-navigation__groups" aria-label={translateLabel("Rubriques de l’accueil")}>
        {filteredGroups.map(group => (
          <section key={group.id} className={`home-navigation__group${(query || expanded[group.id]) && !compact ? " home-navigation__group--open" : ""}`} data-tone={group.id}>
            <button type="button" className="home-navigation__groupToggle" disabled={Boolean(query)} aria-expanded={Boolean((query || expanded[group.id]) && !compact)}
              aria-controls={`${id}-${group.id}`} title={compact ? translateLabel(group.title) : undefined}
              onClick={() => { if (compact) { setCollapsed(false); setExpanded(value => ({ ...value, [group.id]: true })); } else setExpanded(value => ({ ...value, [group.id]: !value[group.id] })); }}>
              <EternuaIcon name={group.icon} /><span className="home-navigation__groupCopy"><strong>{translateLabel(group.title)}</strong><small>{translateLabel(group.subtitle)}</small></span>
              <span className="home-navigation__chevron" aria-hidden="true">⌄</span>
            </button>
            <ul id={`${id}-${group.id}`} hidden={compact || (!query && !expanded[group.id])}>
              {group.links.map(item => (
                <li key={item.label}><Link to={item.to || group.fallbackTo || "/"} onClick={() => setMobileOpen(false)}>
                  <EternuaIcon name={item.icon || iconForPath(item.to || group.fallbackTo)} /><span>{translateLabel(item.label)}</span>
                  {!item.to ? <small className="home-navigation__preview">{translateLabel("Aperçu")}</small> : null}
                </Link></li>
              ))}
            </ul>
          </section>
        ))}
      </nav>
      {!compact && <p className="home-navigation__footer">{translateLabel("Un quotidien plus simple.")}<br /><span>{translateLabel("Un futur à imaginer ensemble.")}</span></p>}
    </aside>
  );

  return (
    <div ref={layoutRef} className={`home-navigation-layout${compact ? " home-navigation-layout--compact" : ""}`}>
      {mobile ? <>
        <div className="home-navigation-layout__mobileBar"><button ref={triggerRef} type="button" aria-expanded={mobileOpen}
          aria-controls={`${id}-navigation`} onClick={() => setMobileOpen(true)}>
          <span aria-hidden="true">☰</span><span>{translateLabel("Explorer Eternua")}</span><small>{translateLabel("Aujourd’hui & Vision3024")}</small>
        </button></div>
        {mobileOpen && createPortal(<div className="home-navigation-overlay"><div className="home-navigation-overlay__backdrop" aria-hidden="true" onClick={() => setMobileOpen(false)} />{panel}</div>, document.body)}
      </> : panel}
      <div className="home-navigation-layout__content">
        {children}
        <HomeFriendsFeed />
      </div>
    </div>
  );
}
